import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Download, FileInput, FileOutput, Hash, Fingerprint, Cpu, Gauge, Wrench, Layers, ShieldCheck, ShieldAlert, Loader2, FileStack, Calendar, User, Wand2, Check, Save, Upload, History, RotateCcw, Star } from "lucide-react";

const hex = (n) => "0x" + n.toString(16).toUpperCase().padStart(6, "0");

export const SolutionDetail = ({ solutionId, services, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState("");
  const svcName = (id) => services.find((s) => s.id === id)?.name || id;

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/solutions/${solutionId}/detail`)
      .then(({ data }) => setD(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [solutionId]);

  const download = async (which) => {
    setDl(which);
    try {
      const url = which === "original" ? `/admin/solutions/${solutionId}/download/original` : `/admin/solutions/${solutionId}/download`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/octet-stream" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = which === "original" ? `ORI_${d.solution.ref}.bin` : (d.solution.mod_filename || `${d.solution.ref}.bin`);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible");
    } finally { setDl(""); }
  };

  const s = d?.solution;
  const [editSvc, setEditSvc] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (d?.solution) setEditSvc(d.solution.services || []); }, [d]);
  const toggleSvc = (id) => setEditSvc((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const dirty = s && editSvc && JSON.stringify([...editSvc].sort()) !== JSON.stringify([...(s.services || [])].sort());
  const applyServices = async () => {
    if (!editSvc || editSvc.length === 0) return toast.error("Sélectionnez au moins une solution");
    setSaving(true);
    try {
      await api.patch(`/admin/solutions/${solutionId}/services`, { services: editSvc });
      toast.success("Solutions corrigées ✅");
      const { data } = await api.get(`/admin/solutions/${solutionId}/detail`);
      setD(data);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  // Editable filenames (original + modified)
  const [oriName, setOriName] = useState("");
  const [modName, setModName] = useState("");
  const [savingNames, setSavingNames] = useState(false);
  useEffect(() => { if (d?.solution) { setOriName(d.solution.original_filename || ""); setModName(d.solution.mod_filename || ""); } }, [d]);
  const namesDirty = s && ((oriName || "") !== (s.original_filename || "") || (modName || "") !== (s.mod_filename || ""));
  const saveNames = async () => {
    if (!modName.trim()) return toast.error("Le nom du fichier modifié est requis");
    setSavingNames(true);
    try {
      await api.patch(`/admin/solutions/${solutionId}/filenames`, { original_filename: oriName, mod_filename: modName });
      toast.success("Noms de fichiers enregistrés ✅");
      const { data } = await api.get(`/admin/solutions/${solutionId}/detail`);
      setD(data);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingNames(false); }
  };

  // Replace the assigned MOD file (keeps same ORI, ref & services)
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(false);
  const [versions, setVersions] = useState([]);
  const [restoring, setRestoring] = useState("");
  const loadVersions = () => api.get(`/admin/solutions/${solutionId}/versions`).then(({ data }) => setVersions(data.versions || [])).catch(() => {});
  useEffect(() => { loadVersions(); }, [solutionId]);
  const replaceMod = async (fileObj) => {
    if (!fileObj) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post(`/admin/solutions/${solutionId}/replace_mod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Fichier solution remplacé ✅ (${data.diff_ok ? "diff recalculé" : data.mode})`);
      const res = await api.get(`/admin/solutions/${solutionId}/detail`);
      setD(res.data);
      loadVersions();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remplacement impossible"); }
    finally { setReplacing(false); if (replaceRef.current) replaceRef.current.value = ""; }
  };
  const restoreVersion = async (vid) => {
    setRestoring(vid);
    try {
      await api.post(`/admin/solutions/${solutionId}/versions/${vid}/restore`);
      toast.success("Version restaurée ✅");
      const res = await api.get(`/admin/solutions/${solutionId}/detail`);
      setD(res.data);
      loadVersions();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restauration impossible"); }
    finally { setRestoring(""); }
  };

  const Row = ({ icon: Icon, label, value, color = "text-white" }) => (
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-zinc-500 shrink-0" />
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 w-28 shrink-0">{label}</span>
      <span className={`font-mono text-xs ${color} truncate`}>{value ?? "—"}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="solution-detail">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="solution-detail-backdrop" />
      <div className="relative w-full max-w-xl h-full bg-ink-base border-l border-white/15 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 bg-ink-base/95 backdrop-blur border-b border-white/10">
          <Hash className="w-5 h-5 text-aurora-cyan" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold text-aurora-cyan tracking-wide">{s?.ref || "…"}</div>
            <div className="font-mono text-[10px] text-zinc-500">Inspection de la solution</div>
          </div>
          <button onClick={onClose} data-testid="solution-detail-close" className="w-9 h-9 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {loading || !d ? (
          <div className="flex items-center justify-center gap-2 py-24 font-mono text-xs text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Quality banner — defects & client satisfaction */}
            {d.quality && (d.quality.defects > 0 || d.quality.avg_rating != null) && (
              <div data-testid="detail-quality" className="flex flex-col sm:flex-row sm:items-center gap-3">
                {d.quality.defects > 0 && (
                  <div className="flex-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 bg-signal-error/10 border border-signal-error/40">
                    <ShieldAlert className="w-4 h-4 text-signal-error shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display text-xs uppercase tracking-tight text-signal-error">Défaut signalé · fiabilité {d.quality.reliability}%</div>
                      <div className="font-mono text-[10px] text-zinc-400">{d.quality.defects}/{d.quality.deliveries} livraison(s) · restaurez une version saine dans l'historique ci-dessous</div>
                    </div>
                  </div>
                )}
                {d.quality.avg_rating != null && (
                  <div data-testid="detail-satisfaction" className="flex items-center gap-2 rounded-md px-3 py-2.5 bg-white/5 border border-white/10">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(d.quality.avg_rating) ? "text-yellow-400 fill-yellow-400" : "text-zinc-600"}`} />
                      ))}
                    </div>
                    <span className="font-mono text-[11px] text-zinc-300">{d.quality.avg_rating}/5</span>
                    <span className="font-mono text-[9px] text-zinc-500">({d.quality.ratings} avis)</span>
                  </div>
                )}
              </div>
            )}
            {/* Services (editable — fix mislabeled imports) */}
            <div className="glass rounded-md p-4" data-testid="detail-services">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Solutions du fichier</span>
                {d.suggested_services?.length > 0 && (
                  <button data-testid="detect-services-btn" onClick={() => setEditSvc(d.suggested_services)}
                    title={`Détecté depuis le nom : ${d.suggested_services.map(svcName).join(", ")}`}
                    className="ml-auto flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-aurora-purple/40 text-aurora-purple hover:bg-aurora-purple/10 transition-colors">
                    <Wand2 className="w-3 h-3" /> Détecter depuis le nom
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {services.map((sv) => {
                  const on = (editSvc || []).includes(sv.id);
                  return (
                    <button key={sv.id} data-testid={`edit-svc-${sv.id}`} onClick={() => toggleSvc(sv.id)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
                      {on && <Check className="w-3 h-3 inline mr-1" />}{sv.name}
                    </button>
                  );
                })}
              </div>
              {dirty && (
                <button data-testid="apply-services-btn" onClick={applyServices} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 mt-1 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Appliquer la correction
                </button>
              )}
            </div>

            {/* Meta */}
            <div className="glass rounded-md p-4 space-y-2.5">
              <Row icon={Fingerprint} label="Marque" value={s.brand || "—"} />
              <Row icon={Cpu} label="ECU" value={s.ecu || "—"} color="text-aurora-purple" />
              <Row icon={Wrench} label="Logiciel" value={s.tuned_with || "—"} />
              <Row icon={Gauge} label="Puissance" value={s.power_hp ? `${s.power_hp} ch` : "—"} />
              <Row icon={User} label="Importé par" value={s.created_by} />
              <Row icon={Calendar} label="Le" value={s.created_at ? new Date(s.created_at).toLocaleString("fr-FR") : "—"} />
            </div>

            {/* Files */}
            <div data-testid="detail-files">
              <div className="flex items-center gap-2 mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Fichiers assignés</div>
                {namesDirty && (
                  <button data-testid="save-filenames-btn" onClick={saveNames} disabled={savingNames}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-[10px] rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
                    {savingNames ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer les noms
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="glass rounded-md p-4 border-l-2 border-aurora-purple">
                  <div className="flex items-center gap-2 mb-3"><FileInput className="w-4 h-4 text-aurora-purple" /><span className="font-display text-sm uppercase tracking-tight">Original (ORI)</span></div>
                  <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">Nom du fichier original</label>
                  <input data-testid="ori-filename-input" value={oriName} onChange={(e) => setOriName(e.target.value)}
                    placeholder="ex. VW_Golf_EDC17_ori.bin"
                    className="w-full bg-black/30 border border-white/10 focus:border-aurora-purple/60 rounded-sm px-2.5 py-1.5 font-mono text-[11px] text-white outline-none mb-3 transition-colors" />
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">SHA-256 · {s.original_size} octets</div>
                  <div className="font-mono text-[11px] text-zinc-300 break-all mb-3">{(s.original_hash || "").slice(0, 32)}…</div>
                  <button data-testid="download-original-btn" disabled={!d.ori_reconstructible || dl === "original"} onClick={() => download("original")}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-aurora-purple/50 text-aurora-purple font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-aurora-purple/10 transition-colors disabled:opacity-40">
                    {dl === "original" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {d.ori_reconstructible ? "Télécharger ORI" : "Non reconstructible"}
                  </button>
                </div>
                <div className="glass rounded-md p-4 border-l-2 border-signal-success">
                  <div className="flex items-center gap-2 mb-3"><FileOutput className="w-4 h-4 text-signal-success" /><span className="font-display text-sm uppercase tracking-tight">Solution assignée (MOD)</span></div>
                  <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block">Nom du fichier solution</label>
                  <input data-testid="mod-filename-input" value={modName} onChange={(e) => setModName(e.target.value)}
                    placeholder="ex. VW_Golf_stage1.bin"
                    className="w-full bg-black/30 border border-white/10 focus:border-signal-success/60 rounded-sm px-2.5 py-1.5 font-mono text-[11px] text-white outline-none mb-3 transition-colors" />
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">SHA-256</div>
                  <div className="font-mono text-[11px] text-zinc-300 break-all mb-3">{(d.mod_hash || "").slice(0, 32)}…</div>
                  <button data-testid="download-mod-btn" disabled={dl === "mod"} onClick={() => download("mod")}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-signal-success/50 text-signal-success font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-signal-success/10 transition-colors disabled:opacity-40">
                    {dl === "mod" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Télécharger MOD
                  </button>
                  <input ref={replaceRef} type="file" className="hidden" data-testid="replace-mod-input" onChange={(e) => replaceMod(e.target.files?.[0])} />
                  <button data-testid="replace-mod-btn" disabled={replacing || !d.ori_reconstructible} onClick={() => replaceRef.current?.click()}
                    title={d.ori_reconstructible ? "Uploader un fichier solution corrigé (garde le même original et la même réf)" : "Original non reconstructible (fichier en clone)"}
                    className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-2 border border-aurora-cyan/50 text-aurora-cyan font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-aurora-cyan/10 transition-colors disabled:opacity-40">
                    {replacing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Remplacer le MOD
                  </button>
                </div>
              </div>
            </div>

            {/* Diff */}
            <div className="glass rounded-md p-4" data-testid="detail-diff">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-aurora-cyan" />
                <span className="font-display text-sm uppercase tracking-tight">Différentiel</span>
                {s.diff_mode === "diff"
                  ? <span className="ml-auto font-mono text-[10px] text-signal-success">{s.diff_count} octets · {d.region_count} zone{d.region_count > 1 ? "s" : ""}</span>
                  : <span className="ml-auto font-mono text-[10px] text-signal-processing uppercase">{s.diff_mode}</span>}
              </div>
              {d.regions.length > 0 && (
                <>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Zones modifiées</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {d.regions.slice(0, 40).map((r, i) => (
                      <span key={i} className="font-mono text-[10px] text-aurora-cyan bg-aurora-cyan/5 border border-aurora-cyan/20 rounded-sm px-1.5 py-0.5">
                        {hex(r.start)}{r.count > 1 ? `–${hex(r.end)}` : ""} <span className="text-zinc-500">({r.count}o)</span>
                      </span>
                    ))}
                    {d.region_count > 40 && <span className="font-mono text-[10px] text-zinc-500">+{d.region_count - 40} autres</span>}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Aperçu octets (avant → après)</div>
                  <div className="max-h-40 overflow-y-auto border border-white/10 rounded-sm">
                    <table className="w-full font-mono text-[11px]">
                      <tbody>
                        {d.sample.map((x, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="px-3 py-1 text-zinc-500">{hex(x.offset)}</td>
                            <td className="px-3 py-1 text-signal-error">{x.from}</td>
                            <td className="px-2 py-1 text-zinc-600">→</td>
                            <td className="px-3 py-1 text-signal-success">{x.to}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Version history */}
            <div className="glass rounded-md p-4" data-testid="detail-versions">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-aurora-purple" />
                <span className="font-display text-sm uppercase tracking-tight">Historique des versions</span>
                <span className="ml-auto font-mono text-[10px] text-zinc-500">{versions.length} archivée{versions.length > 1 ? "s" : ""}</span>
              </div>
              {versions.length === 0 ? (
                <p className="font-mono text-[11px] text-zinc-500">Aucune version antérieure. Chaque remplacement du MOD sera archivé ici.</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((v) => (
                    <div key={v.id} data-testid={`version-row-${v.id}`} className="flex items-center gap-3 bg-black/20 border border-white/5 rounded-sm px-3 py-2">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${v.action === "restore" ? "bg-aurora-purple" : "bg-signal-processing"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[11px] text-zinc-300 truncate">{v.mod_filename || "mod.bin"}</div>
                        <div className="font-mono text-[9px] text-zinc-500 truncate">
                          {(v.mod_hash || "").slice(0, 12)}… · {v.diff_count ?? "?"}o · {v.archived_at ? new Date(v.archived_at).toLocaleString("fr-FR") : "—"}
                          {v.archived_by ? ` · ${v.archived_by}` : ""}
                        </div>
                      </div>
                      <button data-testid={`restore-version-${v.id}`} onClick={() => restoreVersion(v.id)} disabled={!!restoring}
                        title="Restaurer cette version comme fichier solution actuel"
                        className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-aurora-purple/40 text-aurora-purple hover:bg-aurora-purple/10 transition-colors disabled:opacity-50 shrink-0">
                        {restoring === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restaurer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Checksum + usage */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="glass rounded-md p-4" data-testid="detail-checksum">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Checksum</div>
                {d.checksum.validated
                  ? <div className="flex items-center gap-2 text-signal-success"><ShieldCheck className="w-4 h-4" /><span className="font-mono text-xs">Algo validé ({d.checksum.family})</span></div>
                  : <div className="flex items-center gap-2 text-signal-processing"><ShieldAlert className="w-4 h-4" /><span className="font-mono text-xs">À valider en atelier</span></div>}
              </div>
              <div className="glass rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Utilisation</div>
                <div className="flex items-center gap-2 text-white"><FileStack className="w-4 h-4 text-aurora-cyan" /><span className="font-mono text-xs">{d.orders_using} commande(s) sur cet ORI · {d.orders_applied} livrée(s)</span></div>
              </div>
            </div>

            {/* Variants */}
            {d.variants.length > 1 && (
              <div className="glass rounded-md p-4" data-testid="detail-variants">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Autres variantes du même original ({d.variants.length})</div>
                <div className="space-y-1.5">
                  {d.variants.map((v) => (
                    <div key={v.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm ${v.id === s.id ? "bg-aurora-cyan/10 border border-aurora-cyan/30" : "bg-black/20"}`}>
                      <span className="font-mono text-xs text-aurora-cyan font-bold">{v.ref}</span>
                      <span className="flex-1 flex flex-wrap gap-1">{(v.services || []).map((sv) => <span key={sv} className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">{svcName(sv)}</span>)}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{v.diff_mode === "diff" ? `${v.diff_count}o` : v.diff_mode}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
